import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FeeDetails, GUID, PersonOrg } from '@ndvr/billing-api';

import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { DropdownMenu, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { formatCurrency, parseLocalDate, useRequiredParams } from 'utils';
import { useFeeDetails } from 'views/billing/billing-queries';
import { BasicFeeDetails } from 'views/household/BillingHistoryTab/BasicFeeDetails';
import { FeeAmendmentDrawer } from 'views/household/BillingHistoryTab/FeeAmendmentDrawer';
import { Button, Card, CondensedTable, DateTimeDisplay, HorizontalLayout, Title, VerticalLayout } from 'widgets';

export const FeeDetailsTab = () => {
    const navigate = useNavigate();

    const { globalAccountGUID, feeEntryGUID } = useRequiredParams(['globalAccountGUID', 'feeEntryGUID']);
    const { data } = useFeeDetails(globalAccountGUID);

    const [feeToAmend, setFeeToAmend] = useState<FeeDetails>();

    const fee = data?.find((feeDetails) => feeDetails.feeEntry.feeEntryGUID === feeEntryGUID);

    return (
        <VerticalLayout $gap={16}>
            <HorizontalLayout $apart $rowCenter>
                <Title section>Fee Details</Title>
                <Button buttonType="secondary" onClick={() => navigate('..')}>
                    Back
                </Button>
            </HorizontalLayout>
            {fee && (
                <>
                    <BasicFeeDetails fee={fee} twoCol />
                    <FeeDetailsTable fee={fee} setFeeToAmend={setFeeToAmend} />
                </>
            )}
            {feeToAmend && <FeeAmendmentDrawer fee={feeToAmend} onClose={() => setFeeToAmend(undefined)} />}
        </VerticalLayout>
    );
};

const columnHelper = createColumnHelper<FeeDetailsRow>();

const FeeDetailsTable = ({ fee, setFeeToAmend }: { fee: FeeDetails; setFeeToAmend: (fee: FeeDetails) => void }) => {
    const rows = useMemo(() => buildFeeDetailsTableRows(fee), [fee]);

    const columns = [
        columnHelper.display({
            id: 'label',
            cell: ({ row }) => row.original.label,
            minSize: 320,
        }),
        columnHelper.display({
            id: 'amount',
            header: 'Amount',
            cell: ({ row }) => formatCurrency(row.original.feeAmount, fee.feeEntry.currency, 2),
        }),
        columnHelper.display({
            id: 'settled',
            header: 'Settled',
            cell: ({ row }) => formatCurrency(row.original.settledFeeAmount, fee.feeEntry.currency, 2),
        }),
        columnHelper.display({
            id: 'totalOutstanding',
            header: 'Total Outstanding',
            cell: ({ row }) => formatCurrency(row.original.totalOutstandingAmount, fee.feeEntry.currency, 2),
        }),
        columnHelper.display({
            id: 'totalSettled',
            header: 'Total Settled',
            cell: ({ row }) => formatCurrency(row.original.totalSettledAmount, fee.feeEntry.currency, 2),
        }),
        columnHelper.display({
            id: 'date',
            header: 'Date',
            cell: ({ row }) => <DateTimeDisplay dateString={row.original.date} />,
        }),
        columnHelper.display({
            id: 'amend',
            cell: ({ row }) =>
                row.index === 0 && (
                    <Button buttonType="secondary" onClick={() => setFeeToAmend(fee)}>
                        Amend
                    </Button>
                ),
            minSize: 120,
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <DropdownMenu>
                    <EllipsisDropdownMenuTrigger />
                    <DropdownMenuContent>
                        <CopyGuidDropdownMenuItem guid={fee.feeEntry.feeEntryGUID} />
                        {row.original.amendedBy && (
                            <CopyGuidDropdownMenuItem
                                label={`Copy amended by GUID (${row.original.amendedBy.slice(0, 4)}...)`}
                                guid={row.original.amendedBy}
                            />
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        }),
    ];

    return (
        <VerticalLayout $gap={32}>
            <Card $padding={0}>
                <CondensedTable columns={columns} data={rows} />{' '}
            </Card>
            {fee.positiveAmendments.length > 0 && (
                <VerticalLayout $gap={16}>
                    <Title content>Positive amendments</Title>
                    {fee.positiveAmendments.map((feeDetails) => (
                        <FeeDetailsTable
                            key={feeDetails.feeEntry.feeEntryGUID}
                            fee={feeDetails}
                            setFeeToAmend={setFeeToAmend}
                        />
                    ))}
                </VerticalLayout>
            )}
        </VerticalLayout>
    );
};

interface FeeDetailsRow {
    label: string;
    feeAmount?: NumberStr;
    settledFeeAmount?: NumberStr;
    totalOutstandingAmount?: number;
    totalSettledAmount?: number;
    date: DateStr;
    amendedBy?: GUID<PersonOrg>;
}

const buildFeeDetailsTableRows = (feeDetails: FeeDetails): FeeDetailsRow[] => {
    const initialRow: FeeDetailsRow = {
        label: 'Initial amount',
        feeAmount: feeDetails.feeEntry.feeAmount,
        date: feeDetails.feeEntry.createdAt,
        amendedBy: feeDetails.amendedBy,
    };
    const settlementRows: FeeDetailsRow[] = feeDetails.settlements.map((settlement) => ({
        label: 'Settlement',
        settledFeeAmount: settlement.settledFeeAmount,
        date: settlement.settledAt,
    }));
    const amendmentRows: FeeDetailsRow[] = feeDetails.negativeAmendments.map((amendment) => ({
        label: 'Negative amendment' + (amendment.amendedAfterSettlement ? ' (after settlement)' : ''),
        feeAmount: amendment.amendmentAmount,
        settledFeeAmount: amendment.amendedAfterSettlement ? amendment.amendmentAmount : undefined,
        date: amendment.amendedAt,
        amendedBy: amendment.amendedBy,
    }));

    const rows = [initialRow, ...settlementRows, ...amendmentRows].sort(
        (a, b) => parseLocalDate(a.date).getTime() - parseLocalDate(b.date).getTime(),
    );

    let previousTotalOutstandingAmount = 0;
    let previousTotalSettledAmount = 0;
    for (const row of rows) {
        const feeAmount = Number(row.feeAmount ?? 0);
        const settledFeeAmount = Number(row.settledFeeAmount ?? 0);
        row.totalOutstandingAmount = previousTotalOutstandingAmount + feeAmount - settledFeeAmount;
        row.totalSettledAmount = previousTotalSettledAmount + settledFeeAmount;
        previousTotalOutstandingAmount = row.totalOutstandingAmount;
        previousTotalSettledAmount = row.totalSettledAmount;
    }

    return rows;
};
